import { Option } from '@mng/mng-inputs';

export const WERT_DELIMITER = ',';
export const WERT_SUB_DELIMITER = '#';

export const GESCHLECHT_OPTIONS: Option[] = [
  { value: 'M', name: 'Männlich' },
  { value: 'W', name: 'Weiblich' }
];

export const ATTRIBUT_TYPES: { [key: string]: number } = {
  TYPE_BOOLEAN: 1,
  TYPE_NUMERIC: 2,
  TYPE_STRING: 3,
  TYPE_DECIMAL: 4,
  TYPE_LIST: 5,
  TYPE_MULTILIST: 6,
  TYPE_SHORTSTRING: 7,
  TYPE_DATE: 8,
  TYPE_MULTILISTYEAR: 9
} as const;

export const USER_TYPE_ADMIN = 1;
export const USER_TYPE_USER = 2;
// Sonstige für Spezialfälle wie externe User die das MGS API verwenden
export const USER_TYPE_SONSTIGE = 3;

export interface IHasId {
  id?: number;
}

export interface IHasIdAndVersion extends IHasId {
  version?: number;
}

export function idAndVersion(data: IHasIdAndVersion): IHasIdAndVersion {
  return {
    id: data.id,
    version: data.version
  };
}

export interface IHasIdAndLabel extends IHasId {
  label: string;
}

export type IBearbeitungStatus = IHasIdAndLabel;

export interface IGesundheitsProgrammInfo extends IHasIdAndLabel {
  maxPunkte?: number;
  minPunkte?: number;
  maxEinbettzimmerAnzahl?: number;

  aufenthaltDauer: number;

  nichtWechselbar: boolean;

  inaktiv: boolean;

  oeffentlich: boolean;

  dienstleistungsArtId: number;

  kurzBeschreibung?: string;
}

export interface IGesundheitsProgramm extends IGesundheitsProgrammInfo {
  description: string;
  kurzBeschreibung?: string;
  nichtAuswaehlbar?: boolean;
  anzeigeNurFuerBerechtigte?: boolean;
  zusaetzlichWaehlbar?: boolean;
  gruppenZuordnungenIdList?: number[];
}

export interface IDienstleistungArtInfo extends IHasIdAndLabel {
  shortLabel?: string;
  inaktiv: boolean;
  abrechenbar: boolean;
}

export interface IUserInfo extends IHasId {
  name?: string;
  userId?: string;
  istArzt?: boolean;
  email?: string;
  partner?: IPartnerInfo;
}

export interface IUser extends IUserInfo {
  istAerztlicherLeiter?: boolean;
  laborDatenaustausch?: boolean;
  passwortAenderDatum?: string;

  passwortGiltBis?: string;
  loginDatum?: string;

  userTypeId?: number;
  inaktiv?: boolean;
  authorities?: string[];
}

export interface IUserPasswortAenderung {
  // ist im Fall von einem "Direkt" Aufruf über einen URL Parameter gesetzt (Mail wurde angefordert das einen Link enthält)
  idUndPasswortHash?: string;

  // wenn das aktuelle, bestehende Passwort im Passwort Änderungsdialog eingegeben werden muss (KEIN idUndPasswortHash vorhanden)
  // idUndPasswortHash -> wird über eine URL Parameter geliefert
  aktuellesPasswort?: string;

  neuesPasswort: string;
}
export interface ILaborProtokollUebernahmePruefung {
  laborParameterWerte: ILaborParameterValueHolder[];
  laborWerteStimmenMitBefundDatenUeberein: boolean;
}

export interface ILaborProtokollStatusWechsel {
  laborProtokollId: number;
  status: number;
}

export interface ILaborProtokollZuordnungAenderung {
  laborProtokollId: number;
  code: string;
}

export interface IDienstleistungAbschlussInfo {
  abschlussMoeglich: boolean;
  missingAttributDefinitionIds?: number[];
  externePartnerzuordnungNotwendig: boolean;
  punkteHandlingError: boolean;
  laborProtokollUebernahmePruefung?: ILaborProtokollUebernahmePruefung;
  leistung?: ILeistung;
}

export interface IDienstleistungInfo extends IHasIdAndVersion {
  code: string;
  bearbeitungsDatum: string;
  anforderDatum: string;
  aufnahmeDatum?: string;
  giltvonDatum: string;
  giltbisDatum: string;
  buchungsDatum?: string;
  status: IBearbeitungStatus;

  programm: IGesundheitsProgrammInfo;

  dienstleistungsArt: IDienstleistungArtInfo;
  user: IUserInfo;
  kunde: IKunde;

  anmerkungen?: string;

  partner?: IPartnerInfo;

  // Buchung über Kundenportal / Buchung Service
  externeBuchung?: boolean;

  leistungId?: number;

  abrechnungId?: number;

  inaktiv?: boolean;

  managed?: boolean;

  wechselZuBearbeitungStatusErlaubt?: boolean;

  documentId?: string;

  deletable?: boolean;

  anspruchsZeitraumValid?: boolean;

  vertragsNr?: string;

  leistungGesperrt?: boolean;

  // buchbar, rueckBuchbar, fallDefinitionId wird nur bei der Codeprüfung befüllt
  buchbar?: boolean;
  rueckBuchbar?: boolean;
  fallDefinitionId?: number;

  // ist nur ausgefüllt wenn die Daten für die entsprechende View
  // benötigt werden (vor bearbeitung, bei code prüfung)
  //
  wechselProgramme?: IGesundheitsProgrammInfo[];
}

export interface IDienstleistungBuchen {
  dienstleistung: IDienstleistungInfo;
  // Für den Fall einer Buchung über die Buchungszentrale oder als Admin Buchung
  partnerId?: number;
  programmIdFuerWechsel?: number;
}

export interface IAttributWert extends IHasId {
  wert?: string;
}

export interface IAttributWertFuerDefinition extends IAttributWert {
  attributDefId: number;
  normWert?: IAttributNormWert;
}

export type IAttributEinheit = IHasIdAndLabel;

export interface IAttributValidation {
  regexPattern?: string;
  message?: string;
}

export interface IAttributDefinition extends IHasIdAndLabel {
  attributTypeId: number;

  einheit?: IAttributEinheit;

  attributTabelleTypId?: number;

  validation?: IAttributValidation;
  calculated?: boolean; // Werte wird im Backend "berechnet" und nicht eingegeben
  punkteCalculated?: boolean; // Berechneter Wert ist ein "Punktewert"
}

export interface IAttributNormDefinition extends IHasId {
  geschlecht: string;
  comparator: string;
  wert: string;
}

export interface IAttributTabelle extends IHasIdAndLabel {
  inaktiv?: boolean;
}

export interface IAttributDefinitionReference extends IHasId {
  valueForEnablingDependentAttribut: string;

  valueWhenDependentAttributMandatory?: string;

  definitionParentId: number;
}

export interface IAttributNormWert {
  inNorm: boolean;
  wert: string;
}

export interface IAttribut extends IAttributWert {
  definition: IAttributDefinition;
  normDefinition?: IAttributNormDefinition;
  normWert?: IAttributNormWert; // Normwert wird im Backend berechnet

  parentReferences?: IAttributDefinitionReference[];

  attributTabelleList?: IAttributTabelle[];

  required?: boolean; // bedeutet dass es im Context angzeigt werden soll
  mandatory?: boolean; // bedeutet dass es sich um eine "Muss" Eingabe handelt
  punkte?: number;
  disabled?: boolean;
}

export type ISozialversicherung = IHasIdAndLabel;

export interface IKunde extends IHasIdAndVersion, IAdresse {
  name?: string;
  vorname?: string;
  kundennr?: number;
  geburtsDatum?: string;

  alter?: number; // Alter mit "Bezug" z.B. auf das Aufnahmedatum in der Dienstleistung
  titel?: string;

  sex?: string;
  email?: string;
  telefonnr?: string;
  sozialversicherung?: ISozialversicherung;
  sozialversicherung2?: ISozialversicherung;
  datenSpeichern?: boolean;
}

export interface IAttributGruppe extends IHasIdAndLabel {
  definitionen: number[]; // Definition ID's
  childAttributGruppen?: IAttributGruppe[];
}

export type IImpfungGruppe = IHasIdAndLabel;

export interface IImpfungDefinition extends IHasIdAndLabel {
  gruppeId: number;

  zeiteinheit: string;

  folgeimpfungId?: number;
}
export interface IImpfung extends IHasIdAndVersion {
  impfungDefinitionId?: number;
  jahr?: number;
  monat?: number;
}

export interface IImpfungenInfo {
  impfInformationen?: string;

  impfInformationCovid?: string;

  impfinformationzusatz?: boolean;
}

export interface IImpfungenUpdate {
  impfungenInfo?: IImpfungenInfo;

  impfungen?: IImpfung[];
}

export interface IImpfungenContainer extends IImpfungenUpdate {
  impfungGruppen: IImpfungGruppe[];
  impfungDefinitionen: IImpfungDefinition[];
}

export type IDienstleistungResult = IResult<IDienstleistung>;

export interface IDienstleistungBearbeitung {
  code: string;
  aufnahmeDatum: string;
  programmIdFuerWechsel?: number;
}

export type LeistungsArtInfo = IHasIdAndLabel;

export interface IDienstleistungLeistungArtZuordnung extends IHasIdAndVersion {
  partnerExtern: IPartnerInfo;
  leistungArt: LeistungsArtInfo;
}

export interface IDienstleistung extends IDienstleistungInfo {
  bearbeitbar: boolean;

  attribute: IAttribut[];
  attributGruppen: IAttributGruppe[];
  vertragsNr: string;

  vorDienstleistungen: IDienstleistungInfo[];

  impfungen: IImpfungenContainer;

  einbettzimmerzuschlagEnabled: boolean;

  einbettzuschlaganzahl: number;

  einbettzimmerzuschlag: boolean;

  demoDienstleistung: boolean;
  externeZuordnungen: IDienstleistungLeistungArtZuordnung[];

  abschlussBerichtVerfuegbar: boolean;
  textbausteineVerfuegbar: boolean;

  laborProtokolle: ILaborProtokollInfo[];
  vorUntersuchungsAttribute: IAttributWertFuerDefinition[];

  // ID von KidsPotentielle wenn es sich um KIDS handelt
  kidsId?: number;

  // vorhanden wenn dienstleistung "vor bearbeitung" status
  fruehestesAnspruchVonDatum?: string;

  arztWechselCheckRequired?: boolean;
  arzt?: IUserInfo;
  datenSpeichern?: boolean;
}

export interface IDienstleistungHistory {
  code: string;
  programmInfo: string;
  bearbeitungAm: string;
  kundeInfo: string;
}

export interface IDienstleistungUpdate {
  id: number;
  version: number;
  attribute?: IAttributWertFuerDefinition[];
  impfungen?: IImpfungenUpdate;
}

export interface IAdresse {
  strasse?: string;

  ikz?: string;

  ort?: string;

  plz?: string;
}

export interface IPartnerInfo extends IHasId, IAdresse {
  name?: string;

  art?: string;

  bundeslandId?: number;
  bundesland?: string;

  adresse?: string;

  betriebstellenkey?: string;

  extern?: boolean;

  inaktiv?: boolean;
  leistungRelevant?: boolean;
  time4meGruppen?: IHasIdAndLabel[];
  buchungPartner?: number;
}

export interface IPartner extends IPartnerInfo {
  version?: number;

  // hat historische Gründe (MGS) warum als betriebsstellenKey bezeichnet (vs betriebsstellenkey in IPartnerInfo)
  betriebsstellenKey?: string;

  email?: string;

  web?: string;

  telefon?: string;

  fax?: string;

  oeffnungszeiten?: string;

  adressZusatzInfo?: string;

  leistungsabrechnungAb?: string | undefined;

  leistungGesperrt?: boolean;

  labordatenAustausch?: boolean;

  labordatenUebernahme?: boolean;

  honorarEinsichtVerboten?: boolean;

  einbettzimmerZuschlagErlaubt?: boolean;

  hatExternePartner?: boolean;

  emailStandort?: string;
}

export interface IDienstleistungKundeUpdate extends IHasIdAndVersion {
  kunde: IKunde;
}

export type MessageType = 'error' | 'info';

export interface IMessage {
  message?: string;
  type?: MessageType;
  key?: string;
}

export interface ILeistungAbrechnungInfo {
  id: number;
  anforderungsDatum: string;
  gesamtBetragBrutto: number;
  user: IUserInfo;
  // Info welche Programme in der Abrechnung enthalten sind (String mit ";" getrennte
  // Programm ID's) -> nur befüllt wenn die Liste der Abrechnungen ermittelt wird
  programmInfo?: string;
  anzahlLeistungen?: number;
}

export interface ILeistungAbrechnungenParams {
  partnerId?: number;
  vonDatum?: string;
  bisDatum?: string;
}

export interface ILeistungAbrechnung extends ILeistungAbrechnungInfo {
  leistungen: ILeistungInfo[];
}

export interface ILeistungAbrechnungen {
  partner: IPartnerInfo;
  abrechnungen: ILeistungAbrechnungInfo[];
}

export interface ILeistungPosition {
  id?: number;
  betragBrutto: number;
  leistungsArt: string;
  leistungsArtId: number;
  menge: number;
  externerPartnerName?: string; // Befüllt wenn es sich um eine Position für einen externen Partner handelt
}

export interface ILeistungBlockerInfo {
  blockerInfo?: string;
  fallAnzahl?: number;
}

export interface ILeistungInfo {
  id: number | null;
  auszahlungsDatum: string | null;
  aufnahmeDatum: string;
  gesamtBetragBrutto: number;
  dienstleistung: IDienstleistungInfo;
  dienstleistungControlId?: number;
  info?: string;
  externePartnerLeistungsPositionenAvailable?: boolean;
  programmId: number;
}

export interface ILeistungen {
  partner?: IPartnerInfo;
  leistungen: ILeistung[];
  leistungGesperrt?: boolean;
  abrechnung?: ILeistungAbrechnungInfo;
}

export interface ILeistung extends ILeistungInfo {
  leistungPositionen: ILeistungPosition[];
  leistungBlockerInfo?: ILeistungBlockerInfo;
  leistungBetragAnpassungInfo?: string;
  dienstleistungGeloescht?: boolean;
}

export interface ILeistungDefinition extends IHasId {
  giltVon: string;
  giltBis: string;

  betragBrutto: number;

  leistungArtId: number;

  leistungArtLabel: string;

  partnerId: number;
  partnerBetriebsstellenKey: string;

  partnerName: string;

  dienstleistungArtId: number;
  dienstleistungArtLabel: string;
  identNr: string;
}

export interface ILeistungDefinitionContainer {
  leistungDefinitionen: ILeistungDefinition[];
}

export interface ILeistungDefinitionParams {
  partnerId?: number;
  datum?: string;
}

export interface ILeistungAbrechnungParams {
  partnerId?: number;
  stichtag?: string;
  code?: string;
}

export interface IFallPartnerDetail extends IHasIdAndVersion {
  jahr: number;
  anzahl?: number;
  maxAnzahl: number;
  buchungen?: number;
  falldefinitionId: number;
  partnerId: number;
  inBearbeitung?: number;
  abgeschlossen?: number;
  updateDatum: string;
  fallDefinitionBeschreibung: string;
  partnerName: string;
  partnerBetriebsstellenKey: string;
}

export interface IFallPartnerDaten {
  fallPartnerDetailList: IFallPartnerDetail[];
  partner?: IPartnerInfo;
}

export interface ILeistungDefinitionImportParams {
  probe?: boolean;

  minVonDatum?: string;

  minBisDatum?: string;
}

export interface ITextbausteinDefinition extends IHasIdAndVersion {
  label: string;
}

export interface IThemenkreisDefinition extends IHasIdAndVersion {
  label: string;
  textbausteinDefinitionen: ITextbausteinDefinition[];
}

export interface ITextbaustein extends IHasId {
  textbausteinDefinitionId: number;
  selected?: boolean;
}

export interface IThemaIndividuellerText extends IHasIdAndVersion {
  text?: string;
  themenkreisDefinitionId: number;
}

export interface ITextbausteinDaten {
  textbausteine?: ITextbaustein[];
  individuelleTexte?: IThemaIndividuellerText[];
}

export interface ITextbausteinContainer {
  themenkreisDefinitionen: IThemenkreisDefinition[];

  daten: ITextbausteinDaten;
}

export interface ITextbausteinUpdate {
  dienstleistungId: number;
  dienstleistungVersion: number;
  daten: ITextbausteinDaten;
}

export interface IDienstleistungSucheParams {
  code?: string;

  programmId?: number;
  dienstleistungsArtId?: number;

  bearbeitungsStatusId?: number;

  vonDatum?: string;

  bisDatum?: string;

  name?: string;

  vorName?: string;

  bearbeiterId?: number;

  bearbeiterUserId?: string;

  kundennr?: number;
  kundeId?: number;

  geburtsDatum?: string;

  partnerDienstleistungen?: boolean; // Flag für "normale" User nicht nur die eigenen DL zu bekommen

  fuerCodePruefung?: boolean;
}

export type DienstleistungSucheParamKeys = keyof IDienstleistungSucheParams;

export interface IKundeSuche {
  name?: string;
  vorName?: string;
}

export interface IMessages {
  messages?: IMessage[];
}

export interface IVardata extends IMessages {
  data: { [key: string]: any };
}

export type IResult<T> = IVardataWithCustomKey<'result', T>;

export interface IVardataWithCustomKey<TKey extends string, T> extends IVardata {
  data: {
    [key in TKey]: T;
  };
}

export interface ICodetableItem {
  id: number;

  label: string;

  value1?: string;

  value2?: string;

  sort?: number;
  type: string;
}

export interface ILaborProtokollParams {
  partnerId?: number;
  vonDatum?: string;
  bisDatum?: string;
  code?: string;
  statusId?: number;
  allePartnerUntersuchungen?: boolean;
  limit?: number;
}

export type LaborProtokollStatus = ICodetableItem;

export interface ILaborProtokollDaten {
  partner: IPartnerInfo;
  protokolle: ILaborProtokollInfo[];
  stati: LaborProtokollStatus[];
}

export interface ILaborParameter {
  einheit: string;
  code: string;
  displayWert: string;
}

export interface ILaborParameterValueHolder {
  message?: string;
  attribut: IAttribut;
  laborParameter: ILaborParameter;
}

export interface ILaborProtokoll extends ILaborProtokollInfo {
  untersuchungsWerte: ILaborParameterValueHolder[];
  merkurKundenInfo: string;
  anmerkung?: string;
}

export interface ILaborProtokollInfo {
  id: number;

  date: string;

  kundenInfo: string;
  arztInfo: string;

  laborDaten: string;

  status: ICodetableItem;

  dienstleistungsCode: string;
  dienstleistungsId: number;
}

export interface IDienstleistungErfassung {
  programmId?: number;
  giltvonDatum?: string;
  giltbisDatum?: string;
  kunde?: IKunde;
  dokumentAnforderung?: boolean;
}

export interface ICodePruefungByName {
  name: string;
  vorname: string;
  geburtsDatum: string;
}

export interface IGesundheitsPartnerZuordnung extends IHasIdAndVersion {
  gesundheitsPartnerExtern: IPartnerInfo;
  leistungArtIds: number[];
}

export interface IExternePartnerZuordnungDto extends IHasIdAndVersion {
  gesundheitsPartnerExtern: IPartnerInfo;
}

export interface IDienstleistungBasisDatenUpdate extends IHasIdAndVersion {
  aufnahmeDatum?: string;
  einbettzimmerzuschlag?: boolean;
  einbettzuschlaganzahl?: number;
  arztWechsel?: boolean;
}

export interface ILeisungDefinitionImportResult {
  leistungDefinitionenResult: IResult<ILeistungDefinition[]>;
  partner: IPartnerInfo;
}

export interface IMgsClientProps {
  user?: IUser; // wenn der User angemeldet ist
  programme?: IGesundheitsProgrammInfo[]; // Liste der Partner Programme für "normale" User
}

export const MONATE_OPIONS: Option[] = [
  { value: 1, name: 'Jänner' },
  { value: 2, name: 'Februar' },
  { value: 3, name: 'März' },
  { value: 4, name: 'April' },
  { value: 5, name: 'Mai' },
  { value: 6, name: 'Juni' },
  { value: 7, name: 'Juli' },
  { value: 8, name: 'August' },
  { value: 9, name: 'September' },
  { value: 10, name: 'Oktober' },
  { value: 11, name: 'November' },
  { value: 12, name: 'Dezember' }
];

export interface IKidsPotentielle {
  id: number;
  name?: string;
  vorname?: string;
  geburtsDatum?: string; // Assuming LocalDate is converted to string format
  alter?: number;
  sex?: string;
  vertragsnr?: string;
  zusatz1?: string;
  zusatz2?: string;
  anzahlBegleitpersonen?: number;
  anzahlBegleitpersonen2?: number;
  kiviJahr?: number;
  terminStatus?: ICodetableItem;
  version?: number;
  zusatzdaten?: IKidsZusatzdaten;
  kidsTerminId?: number;
  terminZuordnungsDatum?: string;
  kidsTermin?: IKidsTermin;
  ernaehrProtokoll?: boolean;
  onlineReservierung: boolean;
  reservierungLog?: string;
  voruntersuchungJahr?: number;
  mobileReservierung: boolean;
  wiederholer: boolean;
  anzahlWartelisten?: number;
  druckZusageAnforderungDatum?: string;
  druckAbsageAnforderungDatum?: string;
  reservierungGesperrt?: boolean;
}

export interface IKidsZusatzdaten extends IAdresse {
  kundennr?: number;
  id: number;
  name?: string;
  vorname?: string;
  sex?: string;
  anzahlbegleitpersonen?: number;
  zusatz1?: string;
  zusatz2?: string;
  titel?: string;
  email?: string;
  telefonnr?: string;
  loginCode?: string;
  fotoeinverstaendnis?: boolean;
}

export interface IKidsPotentielleDaten {
  kids: IKidsPotentielle[];
}

export interface IKidsPotentielleSuche {
  vertragsNr?: string;
  name?: string;
  vnName?: string;
  kidsTerminId?: number;
  kidsAufWarteliste?: boolean;
}

export interface IKidsTerminInfo {
  id: number;
  date: string;
  hotelBezeichnung: string;
  terminTyp: string;
}

export interface IKidsTermin extends IKidsTerminInfo {
  terminTypId?: number;
  altersGruppe?: string;
  bearbeitungsStatus?: string;
  version?: number;
  anzahlPersonen?: number;
  anzahlPersonenAdmin?: number;

  anzahlGebucht?: number;
  bearbeitungsStatusId?: number;
  hotelId?: number;
  hotelBundesland?: string;
  terminInfo?: string;
  anzahlFix?: number;
  druckZusageAnforderungDatum?: string;
  isTeens?: boolean;
  terminTyp2?: string;
  terminTypZusatzangebot?: string;
  warteListeId?: number;
  versteckt?: boolean;
  anzahlWartelisten?: number;
  sortNr?: number;
}

export interface IKidsHotel extends IAdresse {
  id: number;
  version?: number;
  name?: string;
  detail?: string;
  aktiv?: boolean;
  telefon?: string;
  geolat?: number;
  geolng?: number;
  bundesland?: string;
  homepage?: string;
  adresse?: string;
}

export interface IKidsTermineAnzahlDaten {
  anzahlPersonen?: number;
  anzahlGebucht?: number;
  anzahlFix?: number;
  anzahlTeilgenommen?: number;
  anzahlEntschuldigt?: number;
  anzahlUnentschuldigt?: number;
  anzahlAbgesagt?: number;
}

export interface IKidsBuchungDatenResult {
  data: IKidsBuchungDaten;
}

export interface IKidsPotentielleTerminInfo extends IKidsPotentielle {
  kidsTermine: IKidsTermin[];
}

export interface IKidsBuchungDaten {
  vn: Partial<IKidsZusatzdaten> & { adresse: string };
  buchungAppAvailableUntil: string;
  buchungFrist: number;
  kids: IKidsPotentielleTerminInfo[];
}

export interface IKidsBuchungAppAvailableResult {
  data: IKidsBuchungAppAvailable;
}

export interface IKidsBuchungAppAvailable {
  appAvailableMessage?: string;
  appAvailable?: string;
  appAvailableFrom?: string;
  reservierungMail: string;
}

export interface IKidResult {
  id: number;
  zusatz1?: string;
  zusatz2?: string;
  anzahlBegleitpersonen?: number;
  anzahlBegleitpersonen2?: number;
  version?: number;
}

export interface IKidsReservierung {
  terminId?: number;
  kidDtos: IKidResult[];
  vn?: IKidsZusatzdaten;
  mobile: boolean;
  // Daten für die Warteliste
  hotelId?: number;
  datum?: string;
}

export interface IKidsTerminFuerReservierung extends IKidsTermin {
  anzahlFrei: number;
}

// Alle Termine pro Hotel
export interface IKidsTerminUebersicht {
  key: string;
  date: string;
  hotelBezeichnung: string;
  hotelBundesland: string;
  hotelId: number;
  anzahlFrei: number;
  reservierungMoeglich?: boolean;
  aufWarteListe?: boolean;
  infoText?: string;
  terminList: IKidsTerminFuerReservierung[];
}

export interface IKidsImportInfo {
  existierendeKinderLoeschen: boolean;
}

export interface IBatchInProgressInfo {
  inProgress: boolean;
  startInfo: string | undefined;
}

export interface IKidsDruckAnforderung {
  // wenn kidId und terminId nicht definiert sind dann wird für "alle offenen" die Zusage/Absage durchgeführt
  kidId?: number;
  terminId?: number;
  zusage: boolean;
}

export interface ICmsContentList {
  activeContent: ICmsContent;
  contentHistory: ICmsContent[];
}
export interface ICmsContent {
  id?: number;
  version?: number;
  context: string;
  entityId: string;
  created_at?: string;
  description?: string;
  valid_from?: string;
  comment?: string;
  cmsContent?: string;
}

export interface IAuditlogSearch {
  userId?: number;
  type?: AuditlogType;
  context?: AuditlogContextType;
  requestId?: string;
  key1?: string;
  key2?: string;
  pageNumber: number;
  pageSize: number;
  fromDate?: string;
  toDate?: string;
}

export type AuditlogContextType = 'LOG' | 'DIENSTLEISTUNG' | 'KIDS' | 'AUTH';
export type AuditlogType = 'ERROR' | 'INFO' | 'DEBUG' | 'WARN';
export interface IAuditlog {
  id: number;
  message?: string;
  userId?: number;
  type?: AuditlogType;
  context?: AuditlogContextType;
  requestId?: string;
  key1?: string;
  key2?: string;
  created: Date;

  details?: string;
  userName?: string;
}
